import {
  CheckIcon,
  ClipboardDocumentIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'next-i18next/pages'
import { useState } from 'react'
import { track } from 'track'
import { useAppSelector } from '@/state/hooks'
import { generateAndDownloadImage, type ImageSizeId } from '@/utils/imageUtils'
import { generateMarkdown } from '@/utils/markdownUtils'
import { Drawer } from './Drawer'

interface ShareDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function ShareDrawer({ isOpen, onClose }: ShareDrawerProps) {
  const { t } = useTranslation()
  const options = useAppSelector((state) => state.options)
  const reasons = useAppSelector((state) => state.reasons)
  const fileName = useAppSelector((state) => state.meta.fileName)
  const [copied, setCopied] = useState(false)
  const [includeTitle, setIncludeTitle] = useState(false)
  const [includeBars, setIncludeBars] = useState(true)
  const [imageSize, setImageSize] = useState<ImageSizeId>('auto')
  const hasContent = options.length > 0

  async function handleCopyMarkdown() {
    const md = generateMarkdown(options, reasons, fileName || undefined)
    await navigator.clipboard.writeText(md)
    setCopied(true)
    track('event', 'share-markdown-copy')
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleShareAsImage() {
    await generateAndDownloadImage(options, reasons, fileName || undefined, {
      includeTitle,
      includeBars,
      imageSize,
    })
    track('event', 'share-as-image', {
      include_title: includeTitle ? 1 : 0,
      include_bars: includeBars ? 1 : 0,
      image_size: imageSize,
    })
  }

  const actionBtnClass =
    'w-full flex items-center gap-3 py-3 focus:outline-none focus:ring-2 group text-left disabled:opacity-40 disabled:cursor-not-allowed'

  const checkboxClass =
    'h-3 w-3 rounded-sm border border-[rgba(128,128,128,0.4)] bg-transparent checked:bg-ink dark:checked:bg-dark-ink checked:border-ink dark:checked:border-dark-ink cursor-pointer'

  const labelClass =
    'flex items-center gap-1.5 text-[11px] text-ink-secondary dark:text-dark-ink2 cursor-pointer select-none'

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('Share.title')}
      closeLabel={t('Accessibility.closePanel')}
    >
      <div data-test="share-drawer">
        {process.env.FEATURES_SHOW_SHARE_AS_IMAGE === 'true' && (
          <div className="border-b border-[rgba(128,128,128,0.12)]">
            <button
              type="button"
              data-test="share-as-image-button"
              disabled={!hasContent}
              onClick={handleShareAsImage}
              className={actionBtnClass}
            >
              <PhotoIcon className="h-5 w-5 flex-shrink-0 text-ink-secondary dark:text-dark-ink2 group-hover:group-enabled:text-ink dark:group-hover:group-enabled:text-dark-ink transition-colors" />
              <div>
                <div className="text-[13px] font-semibold tracking-[0.02em] text-ink dark:text-dark-ink transition-colors">
                  {t('Share.shareAsImage')}
                </div>
                <div className="text-[11px] text-ink-secondary dark:text-dark-ink2 mt-0.5">
                  {t('Share.shareAsImageDesc')}
                </div>
              </div>
            </button>
            <div className="pl-8 pb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
              <label className={labelClass}>
                <input
                  type="checkbox"
                  checked={includeTitle}
                  onChange={(e) => setIncludeTitle(e.target.checked)}
                  className={checkboxClass}
                />
                {t('Share.includeTitle')}
              </label>
              <label className={labelClass}>
                <input
                  type="checkbox"
                  checked={includeBars}
                  onChange={(e) => setIncludeBars(e.target.checked)}
                  className={checkboxClass}
                />
                {t('Share.includeBars')}
              </label>
              <div className="flex items-center gap-1.5 text-[11px] text-ink-secondary dark:text-dark-ink2">
                <span>{t('Share.imageSize')}</span>
                <select
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value as ImageSizeId)}
                  className="text-[11px] bg-transparent border border-[rgba(128,128,128,0.25)] rounded px-1.5 py-0.5 text-ink dark:text-dark-ink focus:outline-none focus:ring-1 focus:ring-ink/30"
                >
                  <option value="auto">{t('Share.imageSizeAuto')}</option>
                  <option value="square">{t('Share.imageSizeSquare')}</option>
                  <option value="portrait">
                    {t('Share.imageSizePortrait')}
                  </option>
                  <option value="vertical">
                    {t('Share.imageSizeVertical')}
                  </option>
                  <option value="landscape">
                    {t('Share.imageSizeLandscape')}
                  </option>
                </select>
              </div>
            </div>
          </div>
        )}

        {process.env.FEATURES_SHOW_SHARE_AS_MARKDOWN === 'true' && (
          <button
            type="button"
            data-test="share-markdown-copy-button"
            disabled={!hasContent}
            onClick={handleCopyMarkdown}
            className={`${actionBtnClass} border-b border-[rgba(128,128,128,0.12)]`}
          >
            {copied ? (
              <CheckIcon className="h-5 w-5 flex-shrink-0 text-pro dark:text-pro-dark transition-colors" />
            ) : (
              <ClipboardDocumentIcon className="h-5 w-5 flex-shrink-0 text-ink-secondary dark:text-dark-ink2 group-hover:group-enabled:text-ink dark:group-hover:group-enabled:text-dark-ink transition-colors" />
            )}
            <div>
              <div className="text-[13px] font-semibold tracking-[0.02em] text-ink dark:text-dark-ink transition-colors">
                {copied ? t('Share.copied') : t('Share.markdownCopy')}
              </div>
              <div className="text-[11px] text-ink-secondary dark:text-dark-ink2 mt-0.5">
                {t('Share.markdownCopyDesc')}
              </div>
            </div>
          </button>
        )}
      </div>
    </Drawer>
  )
}
